import { useState } from "react";
import { Badge, CategoryBadge, DataTable, EmptyState, ErrorState, TrialBanner } from "@/components/DataTable";
import { FilterBar, PageHead, SelectField, TextField } from "@/components/Filters";
import { saveSearch, useFilterOptions, useModuleData } from "./hooks";

const INIT = { counselling_body: "", year: "", round: "", college: "", branch: "", quota: "", category: "" };

export default function ClosingRanks() {
  const opts = useFilterOptions();
  const [f, setF] = useState(INIT);
  const [sort, setSort] = useState({ by: "closing_rank", dir: 1 });
  const { rows, total, locked, visible, loading, error } = useModuleData("/closing-ranks", {
    ...f,
    sort_by: sort.by,
    sort_dir: sort.dir,
  });
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  const rank = (v) => (v == null ? <span className="text-slate-300">NA</span> : Number(v).toLocaleString("en-IN"));
  const text = (v) => (v == null || v === "" ? "NA" : v);
  const columns = [
    {
      key: "college",
      label: "College",
      minWidth: 340,
      render: (r) => (
        <span className="block max-w-[420px] whitespace-normal" title={r.college}>
          {text(r.college)}
        </span>
      ),
    },
    { key: "state", label: "State", width: 110, render: (r) => <span className="text-slate-600">{text(r.state)}</span> },
    { key: "branch", label: "Branch", minWidth: 170, render: (r) => text(r.branch) },
    { key: "quota", label: "Quota", width: 130, render: (r) => (r.quota ? <Badge tone="blue" width="quota">{r.quota}</Badge> : <span className="text-slate-400">NA</span>) },
    { key: "category", label: "Category", width: 100, render: (r) => <CategoryBadge code={r.category} /> },
    { key: "year", label: "Year", width: 70, muted: true, render: (r) => text(r.year) },
    { key: "r1", label: "R1", align: "right", width: 80, mono: true, render: (r) => rank(r.r1) },
    { key: "r2", label: "R2", align: "right", width: 80, mono: true, render: (r) => rank(r.r2) },
    { key: "r3", label: "R3", align: "right", width: 80, mono: true, render: (r) => rank(r.r3) },
    { key: "r4", label: "R4", align: "right", width: 80, mono: true, render: (r) => rank(r.r4) },
    { key: "r5", label: "R5", align: "right", width: 80, mono: true, render: (r) => rank(r.r5) },
    { key: "closing_rank", label: "Closing", align: "right", width: 100, highlight: true, render: (r) => (r.closing_rank == null ? "NA" : Number(r.closing_rank).toLocaleString("en-IN")) },
    { key: "fee", label: "Fees", align: "right", width: 110, mono: true, render: (r) => (r.fee == null ? <span className="text-slate-300">NA</span> : `₹${Number(r.fee).toLocaleString("en-IN")}`) },
  ];

  return (
    <div className="space-y-4" data-testid="closing-ranks-page">
      <PageHead
        title="Closing Ranks"
        subtitle="Generated internally from normalized allotment records — round-wise closing ranks per college, branch, quota and category."
        right={
          <span className="font-mono-num text-sm text-slate-500" data-testid="closing-ranks-count">
            {total.toLocaleString("en-IN")} rows
          </span>
        }
      />
      <FilterBar onReset={() => setF(INIT)} onSave={() => saveSearch("closing_ranks", f)}>
        <SelectField label="Body" testId="cr-filter-body" value={f.counselling_body} onChange={set("counselling_body")} options={opts.counselling_bodies} />
        <SelectField label="Year" testId="cr-filter-year" value={f.year} onChange={set("year")} options={opts.years} />
        <SelectField label="Round" testId="cr-filter-round" value={f.round} onChange={set("round")} options={opts.rounds} />
        <SelectField label="Quota" testId="cr-filter-quota" value={f.quota} onChange={set("quota")} options={opts.quotas} />
        <SelectField label="Category" testId="cr-filter-category" value={f.category} onChange={set("category")} options={opts.category_codes} />
        <TextField label="College" testId="cr-filter-college" value={f.college} onChange={set("college")} placeholder="Search college" />
        <TextField label="Branch" testId="cr-filter-branch" value={f.branch} onChange={set("branch")} placeholder="Search branch" />
      </FilterBar>

      {error && <ErrorState message={error} />}
      {locked && <TrialBanner total={total} visible={visible} />}

      {loading ? (
        <div className="border border-slate-200 bg-white p-10 text-center text-sm text-slate-400">Loading…</div>
      ) : rows.length === 0 ? (
        <EmptyState
          testId="closing-ranks-empty"
          title="No results found"
          hint="Closing ranks appear once an allotment batch is published for the selected context."
        />
      ) : (
        <DataTable
          testId="closing-ranks-table"
          columns={columns}
          rows={rows}
          sort={sort}
          onSort={(key) => setSort((s) => ({ by: key, dir: s.by === key ? -s.dir : 1 }))}
        />
      )}
    </div>
  );
}
